import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../context/api";
import "../styles/Services.css";

const Services = ({
  maxItems,
  showHero = true,
  showViewAll = false,
  showPagination = true,
  withBg = true,
  homeBg = "",
}) => {
  const [winnersData, setWinnersData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const itemsPerPage = 10; // how many winners per page
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const fetchWinnersData = async (page = 1, forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      const cacheKey = `winnersDataCache_v4_page_${page}_limit_${itemsPerPage}`;

      // Read cache first for instant paint, then still fetch fresh data.
      if (!forceRefresh) {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const { data, totalPages, timestamp } = JSON.parse(cached);
            const now = Date.now();

            // Use recent cache for quick UI while background refresh runs.
            if (now - timestamp < 10 * 60 * 1000) {
              setWinnersData(Array.isArray(data) ? data : []);
              setServerTotalPages(totalPages || 1);
              setLoading(false);
            }
          } catch (e) {
            localStorage.removeItem(cacheKey);
          }
        }
      }

      // Fetch fresh data
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await api.get(`/winners2`, {
        headers,
        params: {
          paginate: 1,
          page,
          limit: itemsPerPage,
        },
      });

      const payload = response.data;
      const data = Array.isArray(payload) ? payload : payload?.items || [];
      const totalPages = Array.isArray(payload)
        ? Math.max(1, Math.ceil(data.length / itemsPerPage))
        : payload?.totalPages || 1;

      setWinnersData(data);
      setServerTotalPages(totalPages);

      // Save to localStorage with timestamp
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          data,
          totalPages,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWinnersData(currentPage);
  }, [currentPage]);

  const maskName = (name = "") => {
    if (name.length < 4) return name || "Unknown";
    return `${name.slice(0, 2)}*****${name.slice(-3)}`;
  };

  const formatMoney = (val) =>
    Number(val || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  const formatDate = (raw) => {
    if (!raw) return "Unknown date";
    const d = new Date(raw);
    return isNaN(d) ? String(raw) : d.toLocaleString();
  };

  const buildGroups = (items = []) => {
    const map = new Map();
    for (const it of items) {
      const day = it?.selectedDay || "—";
      const dateKey = it?.NextGameDate || "";
      const timeKey = it?.closingTime || "";
      const key = `${day}__${dateKey}__${timeKey}`;
      if (!map.has(key)) {
        map.set(key, { day, date: dateKey, closingTime: timeKey, items: [] });
      }
      const group = map.get(key);
      group.items.push(it);
    }
    return Array.from(map.values());
  };

  // Pagination logic
  const totalPages = Math.max(1, serverTotalPages);
  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const currentItems = winnersData;

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getVisiblePages = () => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3];
    }

    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }

    return [currentPage - 1, currentPage, currentPage + 1];
  };

  if (!loading && (error || currentItems.length === 0)) {
    return null;
  }

  return (
    <div className={`services-page${withBg ? ' with-bg' : ''}${homeBg ? ` services-home-bg` : ''}`}>
      {showHero && (
        <section className="services-hero">
          <div className="hero-content">
            <h1 className="hero-title">Lottery Winners</h1>
            <p className="hero-subtitle">
              Celebrating our latest grand prize winners
            </p>
          </div>
        </section>
      )}

      <main className="services-section">
        <div className="services-card">
          <header className="styled-header">
            <p className="header-subtext">
              Recent lottery winners and their prizes
            </p>
          </header>

          {loading ? (
            <div className="loading-state" aria-live="polite">
              <div className="loading-spinner"></div>
              <span className="loading-text">Loading winners...</span>
            </div>
          ) : currentItems.length === 0 ? (
            <div className="empty-state">
              <p>No winners to display at this time.</p>
            </div>
          ) : (
            <div className="winners-container">
              {/* Desktop Table Layout */}
              <div className="table-wrapper">
                <table className="winners-table">
                  <thead>
                    <tr>
                      <th className="th-rank">#</th>
                      <th className="th-player">Player</th>
                      <th className="th-numbers">Numbers</th>
                      <th className="th-prize">Prize</th>
                      <th className="th-date">Date</th>
                      <th className="th-stake">Stake</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item, idx) => {
                      const commonSet = new Set(
                        (item.commonNumbers || []).map((n) => String(n))
                      );
                      const rank = indexOfFirst + idx + 1;
                      const rowKey = `${item._id || "winner"}-${item.DateTime || item.NextGameDate || "na"}-${item.closingTime || "na"}-${idx}`;
                      return (
                        <tr key={rowKey} className="winner-row">
                          <td className="td-rank">{rank}</td>
                          <td className="td-player">
                            <div className="player-info">
                              <span className="player-name">{maskName(item.fullName)}</span>
                              <span className="player-day">{item.selectedDay || "—"}</span>
                            </div>
                          </td>
                          <td className="td-numbers">
                            <div className="table-numbers">
                              {Array.isArray(item.selectedNumbers) &&
                                item.selectedNumbers.map((num, i) => (
                                  <span
                                    key={i}
                                    className={`table-chip ${
                                      commonSet.has(String(num)) ? "hit" : ""
                                    }`}
                                  >
                                    {num}
                                  </span>
                                ))}
                            </div>
                          </td>
                          <td className="td-prize">
                            <span className="prize-amount">GHC {formatMoney(item.winPrice ?? item.price)}</span>
                          </td>
                          <td className="td-date">
                            <div className="date-info">
                              <span className="date-text">{item.NextGameDate}</span>
                              <span className="time-text">{item.closingTime}</span>
                            </div>
                          </td>
                          <td className="td-stake">GHC {formatMoney(item.price)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card Layout */}
              <div className="mobile-cards">
                {(() => {
                  const groups = buildGroups(currentItems);
                  return groups.map((group, gIdx) => (
                    <section
                      className="day-group"
                      key={`${group.day}-${group.date}-${group.closingTime}-${gIdx}`}
                    >
                      <header className="day-header">
                        <h3 className="day-title">{group.day}</h3>
                        <time className="day-date">
                          {formatDate(group.date)}{group.closingTime ? ` • ${group.closingTime}` : ""}
                        </time>
                      </header>
                      <div className="winners-list">
                        {group.items.map((item, idx) => {
                          const commonSet = new Set(
                            (item.commonNumbers || []).map((n) => String(n))
                          );
                          const rowKey = `${item._id || "winner"}-${item.DateTime || item.NextGameDate || "na"}-${item.closingTime || "na"}-${idx}`;
                          return (
                            <article
                              className="winner-card"
                              key={rowKey}
                            >
                              <div className="winner-header">
                                <span className="meta-day">
                                  {item.selectedDay || "—"}
                                </span>
                                <span className="winner-name">
                                  {maskName(item.fullName)}
                                </span>
                              </div>
                              <div className="winner-amount">
                                GHC {formatMoney(item.winPrice ?? item.price)}
                              </div>
                              {Array.isArray(item.selectedNumbers) &&
                                item.selectedNumbers.length > 0 && (
                                  <div className="numbers-section">
                                    <div className="number-row">
                                      {item.selectedNumbers.map((num, i) => (
                                        <span
                                          key={i}
                                          className={`number-chip ${
                                            commonSet.has(String(num))
                                              ? "hit"
                                              : ""
                                          }`}
                                        >
                                          {num}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              <footer className="winner-footer">
                                <div className="footer-left">
                                  <span className="game-date">
                                    {item.NextGameDate}
                                  </span>
                                  <span className="closing-time">
                                    {item.closingTime}
                                  </span>
                                </div>
                                <div className="footer-right">
                                  <span className="stake-amount">
                                    Stake:{" "}
                                    <strong>GHC {formatMoney(item.price)}</strong>
                                  </span>
                                </div>
                              </footer>
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  ));
                })()}
              </div>

              {showViewAll && (
                <div className="view-more-section">
                  <button
                    className="view-more-btn"
                    onClick={() => navigate("/services")}
                  >
                    View All Winners →
                  </button>
                </div>
              )}

              {/* Pagination Controls */}
              {showPagination && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {getVisiblePages().map((page) => (
                    <button
                      key={page}
                      className={`pagination-btn ${
                        currentPage === page ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    className="pagination-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Services;
